import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  Form,
  Table,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import axiosInstance from "../Request";
import { URL } from "../api";

const inr = (v) =>
  "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ label, value, sub, color }) => (
  <Card className="card-stats mb-4 shadow">
    <CardBody>
      <span className="h6 text-muted text-uppercase d-block" style={{ letterSpacing: "0.04em" }}>
        {label}
      </span>
      <span className="h2 font-weight-bold mb-0" style={{ color }}>
        {value}
      </span>
      {sub != null && <div className="text-muted small mt-1">{sub}</div>}
    </CardBody>
  </Card>
);

const PaymentReco = () => {
  const today = new Date().toISOString().split("T")[0]; // used for the CSV filename; date fields allow future dates
  // Product is chosen from the sidebar (Reconciliation → Credit Line / Loan) via the ?product= query param.
  const location = useLocation();
  const product = new URLSearchParams(location.search).get("product") === "loan" ? "loan" : "credit_line";
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReco = async (e, live = false) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(URL.PAYMENT_AUDIT_COUNTS, {
        params: { fromDate, toDate, livePeday: live, product },
      });
      setData(res.data);
    } catch (err) {
      console.error("Payment reco fetch failed:", err);
      setError("Could not load payment reconciliation.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // ---- Settled / unsettled transaction list ----
  const [settle, setSettle] = useState("unsettled");
  const [txnType, setTxnType] = useState("");
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [settling, setSettling] = useState(false);

  const fetchList = async (e) => {
    if (e) e.preventDefault();
    setListLoading(true);
    setSelectedIds([]);
    try {
      const res = await axiosInstance.get(URL.PAYMENT_AUDIT_TRANSACTIONS, {
        params: { fromDate, toDate, settle, tranType: txnType || undefined, size: 300 },
      });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Settle list fetch failed:", err);
      setList([]);
    } finally {
      setListLoading(false);
    }
  };

  const toggleRow = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = list.length > 0 && selectedIds.length === list.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : list.map((t) => t.id));

  // Settle = rebuild the mobile's wallet (marks payouts settled, creates wallet + customer id) and re-bill.
  const settleMobiles = async (mobiles, label) => {
    const uniq = [...new Set(mobiles.filter(Boolean))];
    if (!uniq.length) {
      alert("No transactions selected.");
      return;
    }
    if (!window.confirm(`Settle ${label} (${uniq.length} mobile${uniq.length > 1 ? "s" : ""})? This rebuilds their wallet and creates the customer if missing.`))
      return;
    setSettling(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_SETTLE, { mobiles: uniq });
      alert(`Settled ${res.data?.mobilesSettled ?? 0} mobile(s)${res.data?.failed ? `, ${res.data.failed} failed` : ""}.`);
      await fetchList();
    } catch (err) {
      console.error("Settle failed:", err);
      alert("Settle failed.");
    } finally {
      setSettling(false);
    }
  };

  const settleSelected = () =>
    settleMobiles(list.filter((t) => selectedIds.includes(t.id)).map((t) => t.mobile), "selected");

  const settleAll = async () => {
    if (!window.confirm("Settle ALL unsettled payouts across the system? This rebuilds their wallets and creates customers as needed — it can take a while."))
      return;
    setSettling(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_SETTLE_ALL);
      alert(`Settled ${res.data?.mobilesSettled ?? 0} mobile(s)${res.data?.failed ? `, ${res.data.failed} failed` : ""}.`);
      await fetchList();
    } catch (err) {
      console.error("Settle-all failed:", err);
      alert("Settle-all failed.");
    } finally {
      setSettling(false);
    }
  };

  const navigate = useNavigate();

  // ---- Data sync (manual + auto-run scheduler) ----
  const [auto, setAuto] = useState({ enabled: false, intervalMinutes: 15, lastRun: null, lastResult: null });
  const [autoInterval, setAutoInterval] = useState(15);
  const [autoBusy, setAutoBusy] = useState(false);
  const [syncingNow, setSyncingNow] = useState(false);
  const [syncFrom, setSyncFrom] = useState("");
  const [syncTo, setSyncTo] = useState("");

  // ---- Windowed sync scheduler (every 5 min, 08:00-01:00 IST) ----
  const [sched, setSched] = useState({ enabled: false, window: "", withinWindow: false, inFlight: false });
  const [schedBusy, setSchedBusy] = useState(false);

  const loadAutoStatus = async () => {
    try {
      const res = await axiosInstance.get(URL.PAYMENT_AUTO_STATUS);
      if (res.data) {
        setAuto(res.data);
        if (res.data.intervalMinutes) setAutoInterval(res.data.intervalMinutes);
      }
    } catch (err) {
      console.error("Auto-sync status failed:", err);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const res = await axiosInstance.get(URL.PAYMENT_SCHEDULER_STATUS);
      if (res.data) setSched(res.data);
    } catch (err) {
      console.error("Scheduler status failed:", err);
    }
  };

  const toggleScheduler = async (enable) => {
    setSchedBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_SCHEDULER_CONFIG, null, {
        params: { enabled: enable },
      });
      setSched(res.data || {});
    } catch (err) {
      console.error("Scheduler config failed:", err);
      alert("Could not update the sync scheduler.");
    } finally {
      setSchedBusy(false);
    }
  };

  useEffect(() => { loadAutoStatus(); loadSchedulerStatus(); }, []);

  // ---- Wallet limit enhancement (amendment) — customer-wise approval log ----
  const [enhCustId, setEnhCustId] = useState("");
  const [enhNewLimit, setEnhNewLimit] = useState("");
  const [enhReason, setEnhReason] = useState("");
  const [enhBusy, setEnhBusy] = useState(false);
  const [enhHistory, setEnhHistory] = useState([]);
  const [enhMsg, setEnhMsg] = useState("");

  const loadEnhHistory = async (cid) => {
    const id = cid || enhCustId;
    if (!id) return;
    try {
      const res = await axiosInstance.get(`${URL.PAYMENT_ENHANCE_HISTORY}/${id}`);
      setEnhHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Enhancement history failed:", err);
      setEnhHistory([]);
    }
  };

  const doEnhance = async () => {
    if (!enhCustId || !enhNewLimit) { alert("Enter customer id and new limit."); return; }
    setEnhBusy(true); setEnhMsg("");
    try {
      const res = await axiosInstance.post(URL.PAYMENT_ENHANCE_LIMIT, null, {
        params: { customerId: enhCustId, newLimit: enhNewLimit, reason: enhReason },
      });
      setEnhMsg(res.data?.message || res.data?.status || "done");
      loadEnhHistory(enhCustId);
    } catch (err) {
      console.error("Enhance failed:", err);
      setEnhMsg("Enhancement failed: " + (err.response?.data?.message || err.message));
    } finally {
      setEnhBusy(false);
    }
  };

  // ---- Bulk amend all breached merchants (50 per batch) ----
  const [bulkAmendBusy, setBulkAmendBusy] = useState(false);
  const [bulkAmendMsg, setBulkAmendMsg] = useState("");
  const doBulkAmendBreached = async () => {
    if (!window.confirm(
      "Amend ALL breached merchants to ₹2,00,000 in the background?\n\n" +
      "Raises every breached merchant's sanctioned wallet limit and GENERATES their amended " +
      "Agreement / KFS / Sanction documents (originals are kept). Already-amended are skipped.\n\n" +
      "This runs in the background (one click for all) — progress updates below.")) return;
    setBulkAmendBusy(true); setBulkAmendMsg("Starting…");
    try {
      const res = await axiosInstance.post(URL.PAYMENT_AMEND_BREACHED_ALL, null, {
        params: { newLimit: 200000 },
      });
      const d = res.data || {};
      if (d.status === "ALREADY_RUNNING") {
        setBulkAmendMsg("Already running — " + (d.progress || ""));
      } else {
        setBulkAmendMsg("Started in background. Progress: (polling…)");
      }
      // Poll progress every 5s until it reports DONE/ERROR (or ~15 min).
      let ticks = 0;
      const poll = setInterval(async () => {
        ticks++;
        try {
          const st = await axiosInstance.get(URL.PAYMENT_AMEND_BREACHED_STATUS);
          const p = st.data?.progress || "";
          setBulkAmendMsg("Progress: " + p);
          if (!st.data?.running || /^DONE|^ERROR/.test(p) || ticks > 180) {
            clearInterval(poll); setBulkAmendBusy(false);
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (err) {
      console.error("Bulk amend failed:", err);
      setBulkAmendMsg("Bulk amend failed: " + (err.response?.data?.message || err.message));
      setBulkAmendBusy(false);
    }
  };

  // ---- PAN pool upload ----
  const [panFile, setPanFile] = useState(null);
  const [panBusy, setPanBusy] = useState(false);
  const [panResult, setPanResult] = useState(null);

  const uploadPanPool = async () => {
    if (!panFile) { alert("Choose a PAN file first."); return; }
    setPanBusy(true); setPanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", panFile);
      const res = await axiosInstance.post(URL.PAYMENT_PAN_POOL_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPanResult(res.data);
    } catch (err) {
      console.error("PAN upload failed:", err);
      setPanResult({ status: "ERROR", message: err.response?.data?.message || err.message });
    } finally {
      setPanBusy(false);
    }
  };

  // ---- Pincode master upload ----
  const [pincodeFile, setPincodeFile] = useState(null);
  const [pincodeBusy, setPincodeBusy] = useState(false);
  const [pincodeResult, setPincodeResult] = useState(null);
  const uploadPincode = async () => {
    if (!pincodeFile) { alert("Choose a pincode CSV first."); return; }
    setPincodeBusy(true); setPincodeResult(null);
    try {
      const fd = new FormData();
      fd.append("file", pincodeFile);
      const res = await axiosInstance.post(URL.PINCODE_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPincodeResult(res.data);
    } catch (err) {
      console.error("Pincode upload failed:", err);
      setPincodeResult({ status: "ERROR", message: err.response?.data?.message || err.message });
    } finally {
      setPincodeBusy(false);
    }
  };

  // ---- Address-state fill (upload reviewed pincode->state CSV) ----
  const [stateFile, setStateFile] = useState(null);
  const [stateBusy, setStateBusy] = useState(false);
  const [stateResult, setStateResult] = useState(null);
  const uploadStateFill = async () => {
    if (!stateFile) { alert("Choose the state-fill CSV first."); return; }
    setStateBusy(true); setStateResult(null);
    try {
      const fd = new FormData();
      fd.append("file", stateFile);
      const res = await axiosInstance.post(URL.PAYMENT_ADDRESS_STATE_IMPORT, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStateResult(res.data);
    } catch (err) {
      setStateResult({ status: "ERROR", message: err.response?.data?.message || err.message });
    } finally {
      setStateBusy(false);
    }
  };

  // ---- Create address (for merchant customers with NO address row) ----
  const [createFile, setCreateFile] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const uploadCreateAddress = async () => {
    if (!createFile) { alert("Choose the create-address CSV first."); return; }
    setCreateBusy(true); setCreateResult(null);
    try {
      const fd = new FormData();
      fd.append("file", createFile);
      const res = await axiosInstance.post(URL.PAYMENT_ADDRESS_CREATE_IMPORT, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCreateResult(res.data);
    } catch (err) {
      setCreateResult({ status: "ERROR", message: err.response?.data?.message || err.message });
    } finally {
      setCreateBusy(false);
    }
  };

  // ---- Feed import (upload SparkPay/Peday CSV -> saved as feed) ----
  const [feedFile, setFeedFile] = useState(null);
  const [feedSource, setFeedSource] = useState("payout");
  const [feedBusy, setFeedBusy] = useState(false);
  const [feedResult, setFeedResult] = useState(null);

  const uploadFeed = async () => {
    if (!feedFile) { alert("Choose a CSV file first."); return; }
    setFeedBusy(true); setFeedResult(null);
    try {
      const fd = new FormData();
      fd.append("file", feedFile);
      const res = await axiosInstance.post(URL.PAYMENT_FEED_IMPORT, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { source: feedSource },
      });
      setFeedResult(res.data);
    } catch (err) {
      console.error("Feed import failed:", err);
      setFeedResult({ status: "ERROR", message: err.response?.data?.message || err.message });
    } finally {
      setFeedBusy(false);
    }
  };

  // ---- Document backfill (missing agreement / KFS / sanction) ----
  const [backfillMax, setBackfillMax] = useState(5000);
  const [backfill, setBackfill] = useState({ running: false });
  const [backfillBusy, setBackfillBusy] = useState(false);

  const loadBackfillStatus = async () => {
    try {
      const res = await axiosInstance.get(URL.PAYMENT_DOC_BACKFILL_STATUS);
      setBackfill(res.data || {});
    } catch (e) { /* ignore */ }
  };

  const startBackfill = async () => {
    setBackfillBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_DOC_BACKFILL, null, { params: { max: backfillMax } });
      setBackfill(res.data || {});
    } catch (err) {
      alert("Could not start backfill: " + (err.response?.data?.message || err.message));
    } finally {
      setBackfillBusy(false);
    }
  };

  const stopBackfill = async () => {
    setBackfillBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_DOC_BACKFILL_STOP);
      setBackfill(res.data || {});
    } catch (err) {
      /* ignore */
    } finally {
      setBackfillBusy(false);
    }
  };

  // Poll backfill status while it is running.
  useEffect(() => {
    if (!backfill.running) return;
    const id = setInterval(loadBackfillStatus, 5000);
    return () => clearInterval(id);
  }, [backfill.running]);

  // ---- EMI backfill (linked merchant wallets missing a repayment schedule) ----
  const [emiMax, setEmiMax] = useState(5000);
  const [emiBackfill, setEmiBackfill] = useState({ running: false });
  const [emiBusy, setEmiBusy] = useState(false);

  const loadEmiStatus = async () => {
    try {
      const res = await axiosInstance.get(URL.PAYMENT_EMI_BACKFILL_STATUS);
      setEmiBackfill(res.data || {});
    } catch (e) { /* ignore */ }
  };
  const startEmi = async () => {
    setEmiBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_EMI_BACKFILL, null, { params: { max: emiMax } });
      setEmiBackfill(res.data || {});
    } catch (err) {
      alert("Could not start EMI backfill: " + (err.response?.data?.message || err.message));
    } finally { setEmiBusy(false); }
  };
  const stopEmi = async () => {
    setEmiBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_EMI_BACKFILL_STOP);
      setEmiBackfill(res.data || {});
    } catch (err) { /* ignore */ } finally { setEmiBusy(false); }
  };
  useEffect(() => {
    if (!emiBackfill.running) return;
    const id = setInterval(loadEmiStatus, 5000);
    return () => clearInterval(id);
  }, [emiBackfill.running]);

  const toggleAuto = async (enable) => {
    setAutoBusy(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_AUTO_CONFIG, null, {
        params: { enabled: enable, intervalMinutes: autoInterval },
      });
      setAuto(res.data || {});
    } catch (err) {
      console.error("Auto-sync config failed:", err);
      alert("Could not update auto-sync.");
    } finally {
      setAutoBusy(false);
    }
  };

  const runSyncNow = async () => {
    setSyncingNow(true);
    try {
      const params = {};
      if (syncFrom) params.from = syncFrom;
      if (syncTo) params.to = syncTo;
      const res = await axiosInstance.post(URL.PAYMENT_SYNC_RUN, null, { params });
      const d = res.data || {};
      alert(d.status === "SKIPPED"
        ? "A sync cycle is already running."
        : `Sync ${d.status}. Payin ${d.payinSaved ?? 0}, Payout ${d.payoutSaved ?? 0}, Wallet ${d.walletRows ?? 0}.`);
      loadAutoStatus();
    } catch (err) {
      console.error("Run sync failed:", err);
      alert("Run sync failed (a cycle may already be running).");
    } finally {
      setSyncingNow(false);
    }
  };

  const stopSync = async () => {
    setSyncingNow(true);
    try {
      const res = await axiosInstance.post(URL.PAYMENT_SYNC_STOP);
      const n = res.data?.stopped ?? 0;
      alert(n > 0 ? `Stopped ${n} running sync job(s).` : "No running sync cycle to stop.");
      loadAutoStatus();
    } catch (err) {
      console.error("Stop sync failed:", err);
      alert("Stop sync failed.");
    } finally {
      setSyncingNow(false);
    }
  };

  // ---- Report downloads (CSV) ----
  const [exporting, setExporting] = useState("");
  const [repFrom, setRepFrom] = useState(today);
  const [repTo, setRepTo] = useState(today);
  // Repayment has its OWN date range: it filters by EMI due date, so a future range returns the
  // repayments due in that window (unlike the other reports, which key off the withdrawal date).
  const [repayFrom, setRepayFrom] = useState(today);
  const [repayTo, setRepayTo] = useState(today);

  // ---- Credit settlement upload (application-id sheet) ----
  const [csFile, setCsFile] = useState(null);
  const [csResult, setCsResult] = useState(null);
  const [csBusy, setCsBusy] = useState(false);
  const [csProgress, setCsProgress] = useState(null);
  const [fastBusy, setFastBusy] = useState(null); // "PREVIEW" | "COMPARE" | "COMMIT"
  const [fastProgress, setFastProgress] = useState(null);
  const [fastResult, setFastResult] = useState(null);

  const downloadCreditSettlementSample = () => {
    // applicationId (required) maps to the loan/customer. Optionally give the credit's tranId
    // (looked up in payment_transaction) and/or an explicit amount; leave both blank to auto-fetch
    // the customer's credits by mobile.
    const rows = [
      { applicationId: "SC63396504", tranId: "621203819057", utr: "621203819057", amount: "", loanType: "MERCHANT", trxDate: "2026-08-01" },
      { applicationId: "SC50365036", tranId: "", utr: "", amount: 1500, loanType: "PERSONAL", trxDate: "" },
    ];
    const ws = XLSX.utils.json_to_sheet(rows, { header: ["applicationId", "tranId", "utr", "amount", "loanType", "trxDate"] });
    ws["!cols"] = [{ wch: 22 }, { wch: 24 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "credit_settlement");
    XLSX.writeFile(wb, "credit_settlement_sample.xlsx");
  };

  const uploadCreditSettlement = async (commit) => {
    if (!csFile) { alert("Choose an Excel file first (a column named applicationId)."); return; }
    if (commit && !window.confirm("Commit settlement? This applies each application's credits to its loan (principal/interest). This cannot be auto-undone.")) return;
    setCsBusy(true);
    setCsResult(null);
    setCsProgress(null);
    try {
      const form = new FormData();
      form.append("file", csFile);
      // Kick off a background job; the server returns a jobId immediately (no long-held request).
      const start = await axiosInstance.post(`${URL.CREDIT_SETTLEMENT_UPLOAD}?commit=${commit}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = start?.data?.jobId;
      if (!jobId) { setCsResult(start?.data || null); return; } // (old sync server fallback)

      // Poll for progress until DONE / ERROR — the browser never waits on one long request.
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      for (;;) {
        await sleep(2000);
        let st;
        try {
          const poll = await axiosInstance.get(`${URL.CREDIT_SETTLEMENT_UPLOAD}/status/${jobId}`);
          st = poll?.data || {};
        } catch (e) {
          continue; // transient error — keep polling
        }
        if (st.status === "RUNNING") {
          setCsProgress({ processed: st.processed || 0, total: st.total || 0 });
          continue;
        }
        if (st.status === "DONE") { setCsResult(st.result || null); break; }
        if (st.status === "ERROR") { alert("Settlement failed: " + (st.error || "unknown error")); break; }
        if (st.status === "NOT_FOUND") { alert("Job expired or not found — please re-run."); break; }
        break;
      }
    } catch (err) {
      console.error("Credit settlement upload failed:", err);
      alert(err?.response?.data?.error || "Upload failed.");
    } finally {
      setCsBusy(false);
      setCsProgress(null);
    }
  };

  // NEW fast-bulk flow (separate endpoints). mode: PREVIEW | COMPARE | COMMIT. loanType: null | "MERCHANT" | "PERSONAL"
  const uploadFast = async (mode, loanType) => {
    if (!csFile) { alert("Choose an Excel file first (applicationId + amount)."); return; }
    const tag = loanType ? `${loanType} ` : "";
    if (mode === "COMMIT" && !window.confirm(`Fast ${tag}COMMIT writes settlements (payment + both amort tables + both EMI schedules${loanType === "MERCHANT" ? " + wallet credit" : ""}). Proceed?`)) return;
    setFastBusy(loanType ? `${loanType}_${mode}` : mode); setFastResult(null); setFastProgress(null);
    try {
      const base = URL.CREDIT_SETTLEMENT_UPLOAD.replace(/\/upload$/, "/fast");
      const q = loanType ? `?loanType=${loanType}` : "";
      const form = new FormData(); form.append("file", csFile);
      const start = await axiosInstance.post(`${base}/${mode.toLowerCase()}${q}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = start?.data?.jobId;
      if (!jobId) { setFastResult(start?.data || null); return; }
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      for (;;) {
        await sleep(2000);
        let st;
        try { st = (await axiosInstance.get(`${base}/status/${jobId}`))?.data || {}; } catch (e) { continue; }
        if (st.status === "RUNNING") { setFastProgress({ processed: st.processed || 0, total: st.total || 0 }); continue; }
        if (st.status === "ERROR") { alert("Fast " + mode + " failed: " + (st.error || "unknown")); break; }
        setFastResult(st.result || st); break;
      }
    } catch (err) {
      console.error("Fast " + mode + " failed:", err);
      alert(err?.response?.data?.error || ("Fast " + mode + " failed."));
    } finally { setFastBusy(null); setFastProgress(null); }
  };

  const toCsv = (rows) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  };

  const downloadReport = async (report) => {
    setExporting(report.key);
    try {
      // Repayment uses its own due-date range; the other dated reports use the shared Report From/To.
      const from = report.key === "repayment" ? repayFrom : repFrom;
      const to = report.key === "repayment" ? repayTo : repTo;
      const params = report.dated ? { fromDate: from, toDate: to } : {};
      const res = await axiosInstance.get(report.url, { params });
      const rows = Array.isArray(res.data) ? res.data : [];
      if (!rows.length) {
        alert(`No ${report.label} records found${report.dated ? " for the selected dates." : "."}`);
        return;
      }
      const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${report.key}_${today}.csv`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error(`Export ${report.key} failed:`, err);
      alert(`Could not export ${report.label}.`);
    } finally {
      setExporting("");
    }
  };

  // Withdrawal-date reports share the "Report From/To" picker below.
  const REPORTS = [
    { key: "credit_limit", label: "Credit Limit / Withdrawals", url: URL.PAYMENT_EXPORT_WITHDRAWALS, dated: true, color: "primary" },
    { key: "login", label: "Login", url: URL.PAYMENT_EXPORT_LOGINS, dated: true, color: "info" },
    { key: "balance", label: "Balance", url: URL.PAYMENT_EXPORT_BALANCE, dated: true, color: "success" },
    { key: "payin", label: "Payin (Credit) Trx", url: URL.PAYMENT_EXPORT_PAYIN, dated: true, color: "info" },
  ];
  // Repayment is exported separately because it filters on EMI due date (its own picker).
  const REPAYMENT_REPORT = { key: "repayment", label: "Repayment", url: URL.PAYMENT_EXPORT_REPAYMENT, dated: true, color: "warning" };

  useEffect(() => {
    fetchReco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const wallet = data?.walletByType || {};
  const walletAmt = data?.walletAmountByType || {};

  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC</h6>
          <h2 className="text-white mb-0">
            Reconciliation — Merchant Loan
          </h2>
        </CardBody>
      </Card>

      <Card className="shadow mb-4">
        <CardBody>
          <Form onSubmit={fetchReco}>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">From Date</Label>
                <Input type="date" bsSize="sm" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)} />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">To Date</Label>
                <Input type="date" bsSize="sm" value={toDate}
                  onChange={(e) => setToDate(e.target.value)} />
              </FormGroup>
              <Button color="primary" size="sm" type="submit" disabled={loading}>
                {loading ? "Loading…" : "Search"}
              </Button>
              <Button color="success" size="sm" type="button" disabled={loading}
                onClick={(e) => fetchReco(e, true)}>
                {loading ? "…" : "Compare with Peday (live)"}
              </Button>
              <Button color="info" size="sm" type="button"
                onClick={() => navigate("/admin/enrichment-retry")}>
                Retry Enrichment (prefill/PAN/CKYC)
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>

      {error && <Card className="shadow"><CardBody className="text-danger text-center py-4">{error}</CardBody></Card>}

      {data && !error && (
        <>
          <Row>
            <Col xl="3" md="6">
              <StatCard label="Withdrawal (Payout)" value={inr(data.withdrawalAmount)}
                sub={`${data.withdrawalCount || 0} txns`} color="#5e72e4" />
            </Col>
            <Col xl="3" md="6">
              <StatCard label="Processing Fee" value={inr(data.processingFee)}
                sub={`${data.processingFeeCount || 0} txns`} color="#fb6340" />
            </Col>
            <Col xl="3" md="6">
              <StatCard label="GST (18%)" value={inr(data.gstAmount)} color="#f5365c" />
            </Col>
            <Col xl="3" md="6">
              <StatCard label="Total Payout" value={inr(data.totalPayout)}
                sub="withdrawal + fee + GST" color="#2dce89" />
            </Col>
          </Row>

          {data.pedayPayinAmount != null && (
            <Card className="shadow mb-4">
              <CardHeader className="border-0"><h3 className="mb-0">Peday dashboard (live) vs Ours</h3></CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr><th></th><th>Peday (live)</th><th>Ours (payment_transaction)</th><th>Difference</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Payin</strong></td>
                    <td>{inr(data.pedayPayinAmount)} <span className="text-muted small">({String(data.pedayPayinCount || 0)})</span></td>
                    <td>{inr(data.payinAmount)} <span className="text-muted small">({String(data.payin || 0)})</span></td>
                    <td className={Math.abs(Number(data.pedayPayinAmount) - Number(data.payinAmount)) < 1 ? "text-success" : "text-danger"}>
                      {inr(Number(data.pedayPayinAmount || 0) - Number(data.payinAmount || 0))}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Payout</strong></td>
                    <td>{inr(data.pedayPayoutAmount)} <span className="text-muted small">({String(data.pedayPayoutCount || 0)})</span></td>
                    <td>{inr(data.payoutAmount)} <span className="text-muted small">({String(data.payout || 0)})</span></td>
                    <td className={Math.abs(Number(data.pedayPayoutAmount) - Number(data.payoutAmount)) < 1 ? "text-success" : "text-danger"}>
                      {inr(Number(data.pedayPayoutAmount || 0) - Number(data.payoutAmount || 0))}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <CardBody className="text-muted small py-2">
                Peday figures are fetched live from the dashboard feeds; a non-zero difference means our local sync is behind.
              </CardBody>
            </Card>
          )}

          <Row>
            <Col xl="12">
              <Card className="shadow mb-4">
                <CardHeader className="border-0"><h3 className="mb-0">Peday Payin / Payout (transactions)</h3></CardHeader>
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <span>Payin (credits) · {String(data.payin || 0)} txns</span>
                    <strong>{inr(data.payinAmount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between text-muted small mb-2">
                    <span className="pl-3">↳ settled / unsettled</span>
                    <span>{inr(data.payinSettled)} / {inr(data.payinUnsettled)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Payout (debits) · {String(data.payout || 0)} txns</span>
                    <strong>{inr(data.payoutAmount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between text-muted small mb-2">
                    <span className="pl-3">↳ settled / unsettled</span>
                    <span>{inr(data.payoutSettled)} / {inr(data.payoutUnsettled)}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ---- Settled / Unsettled transaction list ---- */}
          <Card className="shadow mb-4">
            <CardHeader className="border-0 d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
              <h3 className="mb-0 mr-3">Transaction list</h3>
              <Input type="select" bsSize="sm" style={{ width: "150px" }} value={settle}
                onChange={(e) => setSettle(e.target.value)}>
                <option value="unsettled">Unsettled</option>
                <option value="settled">Settled</option>
                <option value="all">All</option>
              </Input>
              <Input type="select" bsSize="sm" style={{ width: "140px" }} value={txnType}
                onChange={(e) => setTxnType(e.target.value)}>
                <option value="">All types</option>
                <option value="credit">Payin (credit)</option>
                <option value="debit">Payout (debit)</option>
              </Input>
              <Button color="primary" size="sm" onClick={fetchList} disabled={listLoading || settling}>
                {listLoading ? "Loading…" : "Load / Reload"}
              </Button>
              <Button color="success" size="sm" onClick={settleSelected} disabled={settling || !selectedIds.length}>
                {settling ? "Settling…" : `Settle Selected (${selectedIds.length})`}
              </Button>
              <Button color="warning" size="sm" onClick={settleAll} disabled={settling}>
                {settling ? "Settling…" : "Settle All Unsettled"}
              </Button>
              {list.length > 0 && <span className="text-muted small ml-2">{list.length} rows</span>}
            </CardHeader>
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" /></th>
                  <th>Txn Id</th><th>Type</th><th>Amount</th><th>Unsettled</th>
                  <th>Status</th><th>Customer</th><th>UTR</th><th>Created</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan="9" className="text-center text-muted py-3">No rows — click "Load / Reload"</td></tr>
                ) : (
                  list.map((t) => (
                    <tr key={t.id}>
                      <td><input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => toggleRow(t.id)} /></td>
                      <td>{t.tranId || t.gatewayTransactionId || t.id}</td>
                      <td>{t.tranType === "credit" ? "Payin" : t.tranType === "debit" ? "Payout" : t.tranType}</td>
                      <td>{inr(t.amount)}</td>
                      <td>{t.unsettled == null || Number(t.unsettled) === 0
                        ? <span className="text-danger">Unsettled</span>
                        : inr(t.unsettled)}</td>
                      <td>{t.transactionStatus}</td>
                      <td>{t.customerId || t.mobile || "—"}</td>
                      <td>{t.utr || "—"}</td>
                      <td>{t.createdAt ? String(t.createdAt).replace("T", " ").slice(0, 19) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {/* ---- Data Sync (manual + auto-run) ---- */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0 py-3">
          <h3 className="mb-0">Data Sync</h3>
          <small className="text-muted">Ingests payin/payout, builds wallets, onboards customers (prefill / PAN / CKYC).</small>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="d-flex align-items-end flex-wrap" style={{ gap: "14px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Sync From</Label>
              <Input type="date" bsSize="sm" value={syncFrom}
                onChange={(e) => setSyncFrom(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Sync To</Label>
              <Input type="date" bsSize="sm" value={syncTo}
                onChange={(e) => setSyncTo(e.target.value)} />
            </FormGroup>
            <Button color="primary" size="sm" onClick={runSyncNow} disabled={syncingNow}>
              {syncingNow ? "Syncing…" : "Run Sync Now"}
            </Button>
            <Button color="danger" size="sm" onClick={stopSync} disabled={syncingNow}>
              Stop Sync
            </Button>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Auto-run every (minutes)</Label>
              <Input type="number" bsSize="sm" min={1} max={1440} style={{ width: "120px" }}
                value={autoInterval} onChange={(e) => setAutoInterval(Number(e.target.value) || 1)} />
            </FormGroup>
            {auto.enabled ? (
              <Button color="danger" size="sm" onClick={() => toggleAuto(false)} disabled={autoBusy}>
                {autoBusy ? "…" : "Stop Auto-Sync"}
              </Button>
            ) : (
              <Button color="success" size="sm" onClick={() => toggleAuto(true)} disabled={autoBusy}>
                {autoBusy ? "…" : "Start Auto-Sync"}
              </Button>
            )}
            <div className="small">
              <span className={`badge badge-${auto.enabled ? "success" : "secondary"} mr-2`}>
                {auto.enabled ? `AUTO ON · every ${auto.intervalMinutes} min` : "AUTO OFF"}
              </span>
              {auto.lastRun && (
                <span className="text-muted">
                  Last: {String(auto.lastRun).replace("T", " ").slice(0, 19)}
                  {auto.lastResult ? ` — ${auto.lastResult}` : ""}
                </span>
              )}
            </div>
          </div>

          {/* ---- Upload feed file (backfill a day the live feed can't serve) ---- */}
          <hr className="my-3" />
          <div>
            <h4 className="mb-0">Upload Feed File</h4>
            <small className="text-muted d-block mb-2">
              Upload a SparkPay/Peday export (CSV) and it is saved to the DB exactly like the live feed —
              SUCCESS rows only, deduped by gateway txn id (safe to re-upload). Payouts become wallets on the next build.
            </small>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">Type</Label>
                <Input type="select" bsSize="sm" style={{ width: "130px" }}
                  value={feedSource} onChange={(e) => setFeedSource(e.target.value)}>
                  <option value="payout">Payout</option>
                  <option value="payin">Payin</option>
                </Input>
              </FormGroup>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">CSV file</Label>
                <Input type="file" bsSize="sm" accept=".csv,text/csv"
                  onChange={(e) => setFeedFile(e.target.files?.[0] || null)} />
              </FormGroup>
              <Button color="primary" size="sm" onClick={uploadFeed} disabled={feedBusy}>
                {feedBusy ? "Uploading…" : "Upload & Save"}
              </Button>
              {feedResult && (
                <span className="small">
                  {feedResult.status === "ERROR" ? (
                    <span className="text-danger">Failed: {feedResult.message}</span>
                  ) : (
                    <span className="text-success">
                      {feedResult.source}: parsed {feedResult.parsed}, inserted {feedResult.inserted}
                      {feedResult.skippedNoGtid ? `, skipped ${feedResult.skippedNoGtid}` : ""}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ---- Backfill missing legal documents ---- */}
          <hr className="my-3" />
          <div>
            <h4 className="mb-0">Backfill Missing Documents</h4>
            <small className="text-muted d-block mb-2">
              Generates Loan Agreement / KFS / Sanction Letter for merchant customers missing them and uploads
              to S3. Idempotent (only fills what's missing) — safe to re-run. Newest customers first.
            </small>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">Max customers / run</Label>
                <Input type="number" bsSize="sm" min={1} max={20000} style={{ width: "150px" }}
                  value={backfillMax} onChange={(e) => setBackfillMax(Number(e.target.value) || 1000)} />
              </FormGroup>
              {backfill.running ? (
                <Button color="danger" size="sm" onClick={stopBackfill} disabled={backfillBusy}>
                  {backfillBusy ? "…" : "Stop Backfill"}
                </Button>
              ) : (
                <Button color="primary" size="sm" onClick={startBackfill} disabled={backfillBusy}>
                  {backfillBusy ? "…" : "Start Backfill"}
                </Button>
              )}
              <Button color="secondary" size="sm" outline onClick={loadBackfillStatus} disabled={backfillBusy}>
                Refresh
              </Button>
              <span className="small">
                <span className={`badge badge-${backfill.running ? "success" : "secondary"} mr-2`}>
                  {backfill.running ? "RUNNING" : "IDLE"}
                </span>
                {typeof backfill.processed === "number" && (
                  <span className="text-muted">
                    processed {backfill.processed}/{backfill.candidates ?? "?"} · generated {backfill.generated ?? 0}
                    {backfill.failed ? ` · failed ${backfill.failed}` : ""}
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* ---- Backfill missing EMIs (linked wallets with no repayment schedule) ---- */}
          <hr className="my-3" />
          <div>
            <h4 className="mb-0">Backfill Missing EMIs</h4>
            <small className="text-muted d-block mb-2">
              Creates the withdrawal repayment schedule (both FIP + PIF) for merchant wallets that are linked
              to a customer but have no EMI. Merchant-only, additive (only fills missing) — safe to re-run.
            </small>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">Max wallets / run</Label>
                <Input type="number" bsSize="sm" min={1} max={50000} style={{ width: "150px" }}
                  value={emiMax} onChange={(e) => setEmiMax(Number(e.target.value) || 1000)} />
              </FormGroup>
              {emiBackfill.running ? (
                <Button color="danger" size="sm" onClick={stopEmi} disabled={emiBusy}>
                  {emiBusy ? "…" : "Stop EMI Backfill"}
                </Button>
              ) : (
                <Button color="primary" size="sm" onClick={startEmi} disabled={emiBusy}>
                  {emiBusy ? "…" : "Start EMI Backfill"}
                </Button>
              )}
              <Button color="secondary" size="sm" outline onClick={loadEmiStatus} disabled={emiBusy}>
                Refresh
              </Button>
              <span className="small">
                <span className={`badge badge-${emiBackfill.running ? "success" : "secondary"} mr-2`}>
                  {emiBackfill.running ? "RUNNING" : "IDLE"}
                </span>
                {typeof emiBackfill.processed === "number" && (
                  <span className="text-muted">
                    processed {emiBackfill.processed}/{emiBackfill.candidates ?? "?"} · EMIs created {emiBackfill.emisCreated ?? 0}
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* ---- Fill state from reviewed pincode mapping ---- */}
          <hr className="my-3" />
          <div>
            <h4 className="mb-0">Fill Missing State (from pincode)</h4>
            <small className="text-muted d-block mb-2">
              Upload the reviewed state-fill CSV (address_id, state_code). Sets a customer's state ONLY where
              it's currently blank — never overwrites an existing state. Idempotent — safe to re-upload.
            </small>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">State-fill CSV</Label>
                <Input type="file" bsSize="sm" accept=".csv,text/csv"
                  onChange={(e) => setStateFile(e.target.files?.[0] || null)} />
              </FormGroup>
              <Button color="primary" size="sm" onClick={uploadStateFill} disabled={stateBusy}>
                {stateBusy ? "Uploading…" : "Upload & Fill State"}
              </Button>
              {stateResult && (
                <span className="small">
                  {stateResult.status === "ERROR" ? (
                    <span className="text-danger">Failed: {stateResult.message}</span>
                  ) : (
                    <span className="text-success">
                      filled {stateResult.updated} of {stateResult.parsed}
                      {stateResult.skipped ? `, skipped ${stateResult.skipped}` : ""}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ---- Create address for customers with NO address row ---- */}
          <hr className="my-3" />
          <div>
            <h4 className="mb-0">Create Missing Address (merchant only)</h4>
            <small className="text-muted d-block mb-2">
              For wallet customers who have NO address row. Upload a CSV (customer_id, state_code, and optionally
              pincode, address). Creates a new address row — merchant-only, and skips anyone who already has a
              stated address. Idempotent — safe to re-upload.
            </small>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
              <FormGroup className="mb-0">
                <Label className="mb-1 small font-weight-bold">Create-address CSV</Label>
                <Input type="file" bsSize="sm" accept=".csv,text/csv"
                  onChange={(e) => setCreateFile(e.target.files?.[0] || null)} />
              </FormGroup>
              <Button color="primary" size="sm" onClick={uploadCreateAddress} disabled={createBusy}>
                {createBusy ? "Creating…" : "Upload & Create Address"}
              </Button>
              {createResult && (
                <span className="small">
                  {createResult.status === "ERROR" ? (
                    <span className="text-danger">Failed: {createResult.message}</span>
                  ) : (
                    <span className="text-success">
                      created {createResult.created} of {createResult.parsed}
                      {createResult.skipped ? `, skipped ${createResult.skipped}` : ""}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ---- Windowed Sync Scheduler ON/OFF (every 5 min, 08:00-01:00 IST) ---- */}
          <hr className="my-3" />
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
            <div className="mr-2">
              <h4 className="mb-0">Sync Scheduler</h4>
              <small className="text-muted">Auto-runs a sync every 5 min inside the daily window · survives restarts</small>
            </div>
            {sched.enabled ? (
              <Button color="danger" size="sm" onClick={() => toggleScheduler(false)} disabled={schedBusy}>
                {schedBusy ? "…" : "Turn OFF"}
              </Button>
            ) : (
              <Button color="success" size="sm" onClick={() => toggleScheduler(true)} disabled={schedBusy}>
                {schedBusy ? "…" : "Turn ON"}
              </Button>
            )}
            <span className={`badge badge-${sched.enabled ? "success" : "secondary"}`}>
              {sched.enabled ? "SCHEDULER ON" : "SCHEDULER OFF"}
            </span>
            {sched.window && <span className="small text-muted">Window: {sched.window}</span>}
            {sched.enabled && (
              <span className={`badge badge-${sched.withinWindow ? "info" : "light"}`}>
                {sched.withinWindow ? "in window" : "outside window"}
              </span>
            )}
            {sched.inFlight && <span className="badge badge-warning">cycle running</span>}
            <Button color="link" size="sm" className="p-0" onClick={loadSchedulerStatus}>refresh</Button>
          </div>
        </CardBody>
      </Card>

      {/* ---- PAN Pool Upload ---- */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0 py-3">
          <h3 className="mb-0">PAN Pool Upload</h3>
          <small className="text-muted">Upload a PAN file (CSV / pipe / plain — the first valid PAN per line is taken). Valid, not-already-present PANs are added to the pool as available.</small>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="d-flex align-items-end flex-wrap" style={{ gap: "14px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">PAN file (.csv / .txt)</Label>
              <Input type="file" bsSize="sm" accept=".csv,.txt" onChange={(e) => setPanFile(e.target.files && e.target.files[0])} />
            </FormGroup>
            <Button color="primary" size="sm" onClick={uploadPanPool} disabled={panBusy}>
              {panBusy ? "Uploading…" : "Upload to Pool"}
            </Button>
          </div>
          {panResult && (
            <div className="small mt-3">
              <span className={`badge badge-${panResult.status === "DONE" ? "success" : "danger"} mr-2`}>{panResult.status}</span>
              {panResult.status === "DONE" ? (
                <span>
                  lines read: <b>{panResult.linesRead}</b> · valid PANs: <b>{panResult.validPansInFile}</b> · dup in pool: <b>{panResult.duplicateInPool}</b> · dup in customer: <b>{panResult.duplicateInCustomer}</b> · <span className="text-success">inserted: <b>{panResult.inserted}</b></span> · pool total now: <b>{panResult.poolTotalNow}</b>
                </span>
              ) : (
                <span className="text-danger">{panResult.message}</span>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- Pincode Master Upload ---- */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0 py-3">
          <h3 className="mb-0">Pincode Master Upload</h3>
          <small className="text-muted">Upload a CSV with columns <b>pincode, city, state, district</b> (comma / pipe / tab). The header row is skipped; pincodes already present are kept (idempotent — safe to re-upload). Populates pincode_master for city-by-pincode lookup.</small>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="d-flex align-items-end flex-wrap" style={{ gap: "14px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Pincode CSV (.csv / .txt)</Label>
              <Input type="file" bsSize="sm" accept=".csv,.txt" onChange={(e) => setPincodeFile(e.target.files && e.target.files[0])} />
            </FormGroup>
            <Button color="primary" size="sm" onClick={uploadPincode} disabled={pincodeBusy}>
              {pincodeBusy ? "Uploading…" : "Upload Pincodes"}
            </Button>
          </div>
          {pincodeResult && (
            <div className="small mt-3">
              <span className={`badge badge-${pincodeResult.status === "DONE" ? "success" : "danger"} mr-2`}>{pincodeResult.status}</span>
              {pincodeResult.status === "DONE" ? (
                <span>
                  lines read: <b>{pincodeResult.linesRead}</b> · valid: <b>{pincodeResult.validInFile}</b> · duplicate: <b>{pincodeResult.duplicate}</b> · <span className="text-success">inserted: <b>{pincodeResult.inserted}</b></span> · total now: <b>{pincodeResult.totalNow}</b>
                </span>
              ) : (
                <span className="text-danger">{pincodeResult.message}</span>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- Download Reports ---- */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0 py-3">
          <h3 className="mb-0">Download Reports</h3>
          <small className="text-muted">Pick a date range for the report exports below (filtered by withdrawal date).</small>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="d-flex align-items-end flex-wrap mb-3" style={{ gap: "12px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Report From</Label>
              <Input type="date" bsSize="sm" value={repFrom}
                onChange={(e) => setRepFrom(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Report To</Label>
              <Input type="date" bsSize="sm" value={repTo}
                onChange={(e) => setRepTo(e.target.value)} />
            </FormGroup>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
            {REPORTS.map((r) => (
              <Button key={r.key} color={r.color} size="sm" outline
                disabled={exporting === r.key}
                onClick={() => downloadReport(r)}>
                <i className="fa-solid fa-download mr-1" />
                {exporting === r.key ? "Exporting…" : r.label}
              </Button>
            ))}
          </div>

          <hr className="my-4" />

          {/* Repayment has its own date filter — by EMI due date, so a future range works. */}
          <h4 className="mb-1">Repayment Report</h4>
          <small className="text-muted d-block mb-3">
            Filtered by <strong>EMI due date</strong> — pick a range (future months allowed) to export the repayments falling due in that window.
          </small>
          <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Due From</Label>
              <Input type="date" bsSize="sm" value={repayFrom}
                onChange={(e) => setRepayFrom(e.target.value)} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="mb-1 small font-weight-bold">Due To</Label>
              <Input type="date" bsSize="sm" value={repayTo}
                onChange={(e) => setRepayTo(e.target.value)} />
            </FormGroup>
            <Button color={REPAYMENT_REPORT.color} size="sm" outline
              disabled={exporting === REPAYMENT_REPORT.key}
              onClick={() => downloadReport(REPAYMENT_REPORT)}>
              <i className="fa-solid fa-download mr-1" />
              {exporting === REPAYMENT_REPORT.key ? "Exporting…" : REPAYMENT_REPORT.label}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ---- Credit Settlement Upload (application-id sheet) ---- */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0 py-3">
          <h3 className="mb-0">Credit Settlement Upload</h3>
          <small className="text-muted">
            Upload an Excel with an <strong>applicationId</strong> column. For each application, its
            customer's credit payments are fetched from the transaction table and settled against that
            loan (principal / interest, oldest EMI first). <strong>Preview first</strong>, then Commit.
          </small>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="mb-2">
            <Button color="link" size="sm" className="p-0" onClick={downloadCreditSettlementSample}>
              <i className="fa-solid fa-file-arrow-down mr-1" />Download sample Excel
            </Button>
            <span className="text-muted small ml-2">
              — columns: <code>applicationId</code> (required), optional <code>tranId</code> / <code>utr</code> / <code>amount</code> / <code>loanType</code> (MERCHANT | PERSONAL) / <code>trxDate</code> (YYYY-MM-DD)
            </span>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
            <Input type="file" bsSize="sm" accept=".xlsx,.xls" style={{ maxWidth: "320px" }}
              onChange={(e) => { setCsFile(e.target.files?.[0] || null); setCsResult(null); }} />
            <Button color="info" size="sm" disabled={csBusy || !csFile}
              onClick={() => uploadCreditSettlement(false)}>
              {csBusy ? (csProgress?.total ? `Processing ${csProgress.processed}/${csProgress.total}…` : "Working…") : "Preview (dry-run)"}
            </Button>
            <Button color="success" size="sm" disabled={csBusy || !csFile}
              onClick={() => uploadCreditSettlement(true)}>
              {csBusy ? (csProgress?.total ? `Processing ${csProgress.processed}/${csProgress.total}…` : "Working…") : "Commit"}
            </Button>
            {csBusy && (
              <span className="text-muted small">
                Running in the background — you can keep this tab open; it won't time out.
                {csProgress?.total ? ` (${Math.round((csProgress.processed / csProgress.total) * 100)}%)` : ""}
              </span>
            )}
          </div>

          {/* NEW fast-bulk flow (separate endpoints; index-free speed). Validate with Compare before Commit. */}
          <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: "12px" }}>
            <span className="text-muted small">⚡ Fast bulk (needs <code>amount</code> column):</span>
            <Button color="info" outline size="sm" disabled={!!fastBusy || !csFile} onClick={() => uploadFast("PREVIEW")}>
              {fastBusy === "PREVIEW" ? (fastProgress?.total ? `Preview ${fastProgress.processed}/${fastProgress.total}…` : "Working…") : "Fast Preview"}
            </Button>
            <Button color="warning" outline size="sm" disabled={!!fastBusy || !csFile} onClick={() => uploadFast("COMPARE")}>
              {fastBusy === "COMPARE" ? (fastProgress?.total ? `Compare ${fastProgress.processed}/${fastProgress.total}…` : "Working…") : "Compare vs live (validate)"}
            </Button>
            <Button color="danger" outline size="sm" disabled={!!fastBusy || !csFile} onClick={() => uploadFast("COMMIT")}>
              {fastBusy === "COMMIT" ? (fastProgress?.total ? `Commit ${fastProgress.processed}/${fastProgress.total}…` : "Working…") : "Fast Commit (locked)"}
            </Button>
          </div>
          {/* Merchant / wallet loan repayment — same engine, forces loanType=MERCHANT (+ wallet credit) */}
          <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: "12px" }}>
            <span className="text-muted small">🏬 Merchant / wallet repayment (forces MERCHANT + wallet credit):</span>
            <Button color="info" outline size="sm" disabled={!!fastBusy || !csFile} onClick={() => uploadFast("PREVIEW", "MERCHANT")}>
              {fastBusy === "MERCHANT_PREVIEW" ? (fastProgress?.total ? `Preview ${fastProgress.processed}/${fastProgress.total}…` : "Working…") : "Merchant Preview"}
            </Button>
            <Button color="success" outline size="sm" disabled={!!fastBusy || !csFile} onClick={() => uploadFast("COMMIT", "MERCHANT")}>
              {fastBusy === "MERCHANT_COMMIT" ? (fastProgress?.total ? `Commit ${fastProgress.processed}/${fastProgress.total}…` : "Working…") : "Merchant Commit"}
            </Button>
          </div>
          {fastResult && (
            <div className="mt-2 small">
              <span className="badge badge-secondary mr-2">{fastResult.status}</span>
              {(fastResult.applicationsRead ?? 0)} read ·{" "}
              {(fastResult.wouldSettle ?? fastResult.settled ?? fastResult.matched ?? 0)}{" "}
              {fastResult.status === "COMPARED" ? "matched" : (fastResult.status === "DONE" ? "settled" : "would settle")}
              {fastResult.mismatched != null && <span className="text-danger"> · {fastResult.mismatched} MISMATCH</span>}
              {" "}· {(fastResult.skipped ?? 0)} skipped
            </div>
          )}

          {csResult && (
            <div className="mt-3">
              <div className="mb-2">
                <span className={`badge badge-${csResult.status === "DONE" ? "success" : "secondary"} mr-2`}>
                  {csResult.status}
                </span>
                <span className="text-muted small">
                  {csResult.applicationsRead ?? 0} application(s) ·{" "}
                  {(csResult.creditsSettled ?? csResult.creditsWouldSettle ?? 0)} credit(s){" "}
                  {csResult.status === "DONE" ? "settled" : "would settle"} ·{" "}
                  {csResult.creditsSkipped ?? 0} skipped ·{" "}
                  {csResult.applicationsFailed ?? 0} failed · Total {inr(csResult.amount)}
                </span>
              </div>
              <Table className="align-items-center table-flush" responsive size="sm">
                <thead className="thead-light">
                  <tr><th>Application</th><th>Loan Ref</th><th>Customer</th><th>Credits</th><th>Settled</th><th>Amount</th><th>Result</th></tr>
                </thead>
                <tbody>
                  {(csResult.details || []).map((d, i) => (
                    <tr key={i}>
                      <td>{d.applicationId}</td>
                      <td>{d.loanRef ?? "—"}</td>
                      <td>{d.customerId ?? "—"}</td>
                      <td>{d.creditsFound ?? "—"}</td>
                      <td>{d.settled ?? d.wouldSettle ?? 0}</td>
                      <td>{d.amount != null ? inr(d.amount) : "—"}</td>
                      <td className={String(d.result).startsWith("OK") ? "text-success" : "text-danger"}>{d.result}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default PaymentReco;
